import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import React, { useState } from "react";
import Collapse from "@material-ui/core/Collapse";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { Typography } from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress";
import { format } from "date-fns";
import { convertTZ,convertTZConvertion} from "../planner/plannerHelpers";

const useStyles = makeStyles((theme) => ({
  table: {
    width: "100%",
  },
  muiTableBody: {
    backgroundColor: "#fff",
  },
  tableHead: {
    backgroundColor: "#FFF",
    "& > *": {
      fontSize: 18,
      fontWeight: 700,
      color: "#333333",
      verticalAlign: "bottom",
      paddingBottom: 10,
    },
  },
  tableBody: {
    backgroundColor: "#FFF",
    cursor: "pointer",
    borderBottom: "1px solid #979797",
    "& > *": {
      fontSize: 18,
      fontWeight: 500,
      color: "#14191C",
    },
    "& > :nth-child(3)": {
      fontSize: 18,
      fontWeight: 700,
      color: "#333333",
    },
  },
  loading: {
    width: "100%",
    "& > * + *": {
      marginTop: theme.spacing(2, 0, 0, 50),
    },
  },
  tableCell: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    fontFamily: "Lato",
  },
  tableIcon: {
    color: "#0049a3",
  },
  shadow: {
    // "box-shadow": "0px 0px 5px 2px rgb(180, 180, 180, 0.1)",
    width: "100%",
    display: "flex",
    backgroundColor: "#FFF",
    minHeight: 50,
    alignItems: "center",
    borderRadius: 2,
    "& > *": {
      wordBreak: "break-word",
      textAlign: "justify",
    },
  },
  auditDetails: {
    borderBottomWidth: 1,
    padding: 0,
    paddingTop: 15,
    borderBottom: "1px solid #a3a3a361",
  },
  capitalText: {
    fontWeight: 600,
    color: "#00000091",
    fontSize: 12,
  },
  collapseRow: {
    '& td': {
      backgroundColor: "#f4f5f7",
    },
  },
}));

interface IAuditEntries {
  changedColumnCaptions: any;
  auditId: string;
  userId: string;
  type: string;
  tableName: string;
  dateTime: string;
  primaryKey: string;
  userName: string;
  eventDescription: string;
  changedColumns: string[];
  hasDetails: boolean;
  newFields: any;
  oldFields: any;
  timezone:any;
}

const LoadingDiv = () => {
  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: 20,
          fontSize: 18,
          fontWeight: 500,
        }}
      >
        Loading . . .
      </div>
      <LinearProgress />
    </>
  );
};

const CollapseRow = ({ auditDetails }: { auditDetails: IAuditEntries }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);


  return (
    <React.Fragment key={auditDetails.auditId}>
      <TableRow
        hover
        className={classes.tableBody}
        onClick={() => setOpen((prev) => !prev)}
      >
        <TableCell width="3%" className={classes.tableCell}></TableCell>
        <TableCell width="17%" className={classes.tableCell}>
          {format(new Date(auditDetails.dateTime), "dd/MM/yyyy HH:mm")}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {auditDetails.eventDescription}
        </TableCell>
        <TableCell align="right" width={60} className={classes.tableIcon}>
          <InfoOutlinedIcon />
        </TableCell>
      </TableRow>
      <TableRow className={classes.collapseRow}>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell colSpan={2}>
          <Collapse
            in={open}
            style={{ backgroundColor: "#f4f5f7" }}
            timeout="auto"
            unmountOnExit
          >
            {auditDetails?.changedColumns ? (
              auditDetails?.changedColumns.map((item: string) => {
                return (
                  <div key={item} className={classes.auditDetails}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FiberManualRecordIcon
                        style={{ fontSize: 12, marginRight: 10, color: "#333" }}
                      />
                      <Typography
                        style={{
                          fontWeight: 700,
                          fontSize: 16,
                          color: "#000000",
                        }}
                      >
                        {renameLabels(auditDetails?.eventDescription,auditDetails?.changedColumnCaptions?.[item])} Changed
                      </Typography>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        padding: 20,
                        paddingTop: 5,
                      }}
                    >
                      <div style={{ width: "45%" }}>
                        <Typography className={classes.capitalText}>
                          FROM
                        </Typography>
                        <div className={classes.shadow}>
                          {getResultUI(auditDetails?.eventDescription,auditDetails?.timezone,auditDetails?.oldFields?.[item], item)}
                        </div>
                      </div>
                      <div style={{ width: 30 }}></div>
                      <div style={{ width: "45%" }}>
                        <Typography className={classes.capitalText}>
                          TO
                        </Typography>
                        <div className={classes.shadow}>
                          {getResultUI(auditDetails?.eventDescription,auditDetails?.timezone,auditDetails?.newFields?.[item], item)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <LoadingDiv />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const FormPage = (props: any) => {
  const classes = useStyles();

  return (
    <Box margin={0} {...props}>
      <Table
        size="small"
        aria-label="purchases"
        style={{ tableLayout: "fixed" }}
      >
        <TableHead>
          <TableRow className={classes.tableHead}>
            <TableCell width="3%" className={classes.tableCell}></TableCell>
            <TableCell width="17%" className={classes.tableCell}>
              Date/Time
            </TableCell>
            <TableCell className={classes.tableCell}>
              Event Description
            </TableCell>
            <TableCell
              align="right"
              width={60}
              className={classes.tableCell}
            ></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.auditEntries?.map((item: any) => (
            <CollapseRow auditDetails={item} />
          ))}
        </TableBody>
      </Table>
      {props.loading && <LoadingDiv />}
    </Box>
  );
};

const getResultUI = (eventDescription:any,timezone:any,details: any, key: string) => {
  switch (key) {
    case "SpecialNeeds":
      return (
        (
          <Typography
            style={{
              fontWeight: 700,
              padding: 13,
              borderBottom: "1px solid #F6F6F6",
              fontSize: 14,
            }}
          >
            {details?.map((item: any) => item.SpecialNeedName).join(", ")}
          </Typography>
        ) || ""
      );
    case "Medications": {
      return (
        <div>
          {details?.map((item: any) => {
            return (
              <Typography
                style={{
                  fontWeight: 700,
                  padding: 13,
                  borderBottom: "1px solid #F6F6F6",
                  fontSize: 14,
                }}
              >
                <span style={{ fontWeight: 400 }}>Date/Time: </span>
                {format(new Date(item.DateAndTime), "dd/MM/yyyy HH:mm")}
                {", "}
                <span style={{ fontWeight: 400 }}>Dose: </span>
                {item.Dose}
                {", "}
                <span style={{ fontWeight: 400 }}>Name: </span>
                {item.Name}
                {", "}
                <span style={{ fontWeight: 400 }}>Route: </span>
                {item.Route}
              </Typography>
            );
          }) || null}
        </div>
      );
    }
    case "Observations": {
      return (
        <div>
          {details?.map((item: any) => {
            return (
              <Typography
                style={{
                  fontWeight: 700,
                  padding: 13,
                  borderBottom: "1px solid #F6F6F6",
                  fontSize: 14,
                }}
              >
                <span style={{ fontWeight: 400 }}>Date/Time: </span>
                {format(new Date(item.DateAndTime), "dd/MM/yyyy HH:mm")}
                {", "}
                <span style={{ fontWeight: 400 }}>AVPU: </span>
                {item.AVPU}
                {", "}
                <span style={{ fontWeight: 400 }}>BGL: </span>
                {item.BGL}
                {", "}
                <span style={{ fontWeight: 400 }}>BPDiastolic: </span>
                {item.BPDiastolic}
                {", "}
                <span style={{ fontWeight: 400 }}>BPSystolic: </span>
                {item.BPSystolic}
                {", "}
                <span style={{ fontWeight: 400 }}>CapRefill: </span>
                {item.CapRefill}
                {", "}
                <span style={{ fontWeight: 400 }}>Pain: </span>
                {item.Pain}
                {", "}
                <span style={{ fontWeight: 400 }}>Pulse: </span>
                {item.Pulse}
                {", "}
                <span style={{ fontWeight: 400 }}>RespRate: </span>
                {item.RespRate}
                {", "}
                <span style={{ fontWeight: 400 }}>SP02: </span>
                {item.SP02}
                {", "}
                <span style={{ fontWeight: 400 }}>TEMP: </span>
                {item.TEMP}
                {/* <span style={{ fontWeight: 400 }}>Time: </span>{item.Time} */}
              </Typography>
            );
          }) || null}
        </div>
      );
    }
    case "Birthday":
    case "BookingDate":
    case "ScheduledDate":
      return (
        <Typography style={{ fontWeight: 700, padding: 13, fontSize: 14 }}>
          {eventDescription.includes("Rescheduled")? (details ? format(new Date(details), "dd/MM/yyyy HH:mm:ss")+ " " + getAustralianTimezoneAbbreviation(timezone) : "") : (details ? format(new Date(details), "dd/MM/yyyy HH:mm:ss") : "")}
        </Typography>
      );
    default:
      return (
        <Typography style={{ fontWeight: 700, padding: 13, fontSize: 14 }}>
          {details}
        </Typography>
      );
  }
};

const renameLabels = (eventDescription: string,key: any) => {
if(eventDescription.includes("Rescheduled")) {
  switch (key) {
    case 'Scheduled date':
      return 'ScheduledPickupTime';
    case 'LastRescheduledApprover':
      return 'LastRescheduleApprover';
    case 'LastRescheduledReason':
      return 'LastRescheduleReason';
    default:
      return key;
  }
}
else{
  return key
}
};

const getAustralianTimezoneAbbreviation = (standardName:any) => {

  switch (standardName) {
    case 'AUS Eastern Standard Time':
      return 'AEST';
    case 'AUS Central Standard Time':
      return 'ACST';
    case 'AUS Western Standard Time':
      return 'AWST';
    case 'Tasmania Standard Time':
      return 'AEDT';
    case 'Lord Howe Standard Time':
      return 'LHST';
    case 'Central Australia Standard Time':
      return 'ACDT';
    case 'Eastern Australia Standard Time':
      return 'AEDT';
    case 'West Pacific Standard Time':
      return 'WPST';
    case 'E. Australia Standard Time':
      return 'AEST';
    case 'AUS Central W. Standard Time':
      return 'ACWST';
    default:
      return "";
  }
};


  

export default FormPage;
